/**
 * 청크 로드 에러 발생 시 SPA 내 재시도 → 실패 시 새로고침 처리
 * - 탭 discard 등으로 메모리에서 해제된 chunk → import retry로 복구
 * - 배포로 해시가 바뀐 chunk → retry 실패 시 전체 새로고침 fallback
 * - 무한 새로고침 방지 (최대 3회)
 */
const RELOAD_KEY = '__chunk_reload_count'
const RETRY_KEY = '__chunk_retry_count'
const MAX_RELOAD_COUNT = 3
const MAX_RETRY_COUNT = 2

/**
 * 청크 에러 메시지인지 판별
 */
function isChunkErrorMessage(errorMessage) {
  return (
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('ChunkLoadError') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('Failed to load module script') ||
    errorMessage.includes('MIME type') ||
    errorMessage.includes('Importing a module script failed')
  )
}

/**
 * 전체 새로고침 (retry 불가능한 경우의 fallback)
 */
function fallbackReload(targetUrl) {
  const reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10)

  if (reloadCount < MAX_RELOAD_COUNT) {
    sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1))
    console.warn(`[ChunkErrorHandler] SPA retry 실패, 새로고침 fallback (${reloadCount + 1}/${MAX_RELOAD_COUNT})`)

    if (targetUrl) {
      window.location.href = targetUrl
    } else {
      window.location.reload()
    }
  } else {
    console.error('[ChunkErrorHandler] 최대 새로고침 횟수 초과')
    sessionStorage.removeItem(RELOAD_KEY)
    window.location.href = '/main'
  }
}

export function setupChunkErrorHandler(router) {
  const removeGuards = []

  // ─── 1. Router onError: SPA 내 재시도 (가장 중요) ───
  // 라우트 이동 중 chunk 로드 실패 시 router.replace로 재네비게이션
  if (router && typeof router.onError === 'function') {
    const removeErrorHandler = router.onError((error, to) => {
      const errorMessage = error?.message || ''

      if (!isChunkErrorMessage(errorMessage)) return

      const retryCount = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10)
      const targetUrl = to?.fullPath || to?.path

      if (retryCount < MAX_RETRY_COUNT) {
        sessionStorage.setItem(RETRY_KEY, String(retryCount + 1))
        console.warn(`[ChunkErrorHandler] chunk 로드 실패, SPA 재시도 (${retryCount + 1}/${MAX_RETRY_COUNT}): ${targetUrl}`)

        // router.replace로 재네비게이션 → chunk를 다시 import
        router.replace(targetUrl).catch(() => {
          console.warn('[ChunkErrorHandler] SPA 재시도 실패, 새로고침 fallback')
          fallbackReload(targetUrl)
        })
      } else {
        // retry 횟수 초과 → 배포로 파일이 바뀐 경우이므로 전체 새로고침
        console.warn('[ChunkErrorHandler] SPA 재시도 횟수 초과, 새로고침 fallback')
        sessionStorage.removeItem(RETRY_KEY)
        fallbackReload(targetUrl)
      }
    })

    if (typeof removeErrorHandler === 'function') removeGuards.push(removeErrorHandler)
  }

  // ─── 2. unhandledrejection: 라우터 외부 dynamic import 실패 처리 ───
  // 컴포넌트 내 lazy import 등 router.onError로 잡히지 않는 경로
  const handleWindowChunkError = (event) => {
    const reason = event.reason
    const errorMessage =
      reason?.message ||
      (reason instanceof Error ? reason.toString() : '') ||
      (typeof reason === 'string' ? reason : '') ||
      ''

    // chunk 메시지가 아니면 이 핸들러는 관여하지 않는다
    if (!isChunkErrorMessage(errorMessage)) return

    console.warn('[ChunkErrorHandler] 비라우터 chunk 에러 감지:', errorMessage)

    // router.onError가 먼저 처리할 수 있으니 잠시 양보 후, 그래도 pending이 남아있으면 fallback
    const pendingTarget = sessionStorage.getItem('__chunk_pending_target')
    if (pendingTarget) {
      setTimeout(() => {
        const stillPending = sessionStorage.getItem('__chunk_pending_target')
        if (stillPending) {
          console.warn('[ChunkErrorHandler] router.onError 미처리, window fallback 실행')
          fallbackReload(stillPending)
        }
      }, 1000)
      return
    }

    fallbackReload(null)
  }

  window.addEventListener('unhandledrejection', handleWindowChunkError)

  // ─── 3. 정상 로드 시 카운트 초기화 ───
  const handleWindowLoad = () => {
    sessionStorage.removeItem(RELOAD_KEY)
    sessionStorage.removeItem(RETRY_KEY)
  }
  window.addEventListener('load', handleWindowLoad)

  // ─── 4. beforeEach: 목적지 URL 임시 저장 ───
  if (router && typeof router.beforeEach === 'function') {
    const removeBeforeEach = router.beforeEach((to, _from, next) => {
      sessionStorage.setItem('__chunk_pending_target', to.fullPath)
      next()
    })

    if (typeof removeBeforeEach === 'function') removeGuards.push(removeBeforeEach)
  }

  // ─── 5. afterEach: 성공 시 카운트 & 목적지 초기화 ───
  if (router && typeof router.afterEach === 'function') {
    const removeAfterEach = router.afterEach((_to, _from, failure) => {
      // chunk 에러로 인한 실패가 아니면 pending을 비운다.
      // (chunk 에러는 router.onError에서 별도로 retry/fallback 처리)
      const isChunkFailure =
        failure && isChunkErrorMessage(failure?.message || '')
      if (!isChunkFailure) {
        sessionStorage.removeItem('__chunk_pending_target')
        sessionStorage.removeItem(RETRY_KEY)
        sessionStorage.removeItem(RELOAD_KEY)
      }
    })

    if (typeof removeAfterEach === 'function') removeGuards.push(removeAfterEach)
  }

  return () => {
    window.removeEventListener('unhandledrejection', handleWindowChunkError)
    window.removeEventListener('load', handleWindowLoad)
    removeGuards.forEach((removeGuard) => removeGuard())
  }
}
